import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ModalService } from './modal.service';
import { AuthService } from './auth.service';
import { UserService } from './user.service';
import { AccountService } from './account.service';
import { ROUTE_URLS } from '../app/app.routes.names';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from '../app/app.service';
import { SHOPIFY_TOKENS } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class SessionTimeoutService implements OnDestroy {
  private readonly idleMs = 20 * 60 * 1000; // 20 minutes
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private logoutTimer: ReturnType<typeof setTimeout> | null = null;
  private modalOpen = false;
  private listenersBound = false;

  constructor(
    private modalHelper: ModalService,
    private authService: AuthService,
    private userService: UserService,
    private accountService: AccountService,
    private router: Router,
    private translate: TranslateService,
    private appState: AppState
  ) {}

  start(): void {
    if (!this.isAuthenticated()) {
      this.stop();
      return;
    }
    if (!this.listenersBound) {
      this.bindActivityListeners();
      this.listenersBound = true;
    }
    this.resetIdleTimer();
  }

  stop(): void {
    this.clearTimers();
    this.modalOpen = false;
  }

  ngOnDestroy(): void {
    this.removeActivityListeners();
    this.clearTimers();
  }

  private isAuthenticated(): boolean {
    return !!sessionStorage.getItem('auth_token');
  }

  private bindActivityListeners(): void {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(evt => window.addEventListener(evt, this.resetIdleTimerBound, true));
    document.addEventListener('visibilitychange', this.onVisibilityChangeBound, true);
  }

  private removeActivityListeners(): void {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(evt => window.removeEventListener(evt, this.resetIdleTimerBound, true));
    document.removeEventListener('visibilitychange', this.onVisibilityChangeBound, true);
    this.listenersBound = false;
  }

  private resetIdleTimerBound = (): void => this.resetIdleTimer();
  private onVisibilityChangeBound = (): void => {
    if (!document.hidden) {
      this.resetIdleTimer();
    }
  };

  private resetIdleTimer(): void {
    if (!this.isAuthenticated()) {
      this.stop();
      return;
    }
    this.clearTimers();
    this.idleTimer = setTimeout(() => this.handleIdle(), this.idleMs);
  }

  private clearTimers(): void {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = null;
    }
  }

  private handleIdle(): void {
    if (this.modalOpen || !this.isAuthenticated()) return;
    this.modalOpen = true;

    const customHTML = `
      <p class="desc"> ${this.translate.instant('alert-security-modal.message')} <b>${this.translate.instant('alert-security-modal.signed-in')}</b> <span>${this.translate.instant('alert-security-modal.or-to')}</span> <b>${this.translate.instant('alert-security-modal.logout')}</b></p>
      <p class="desc last">${this.translate.instant('alert-security-modal.otherwise')} <b>${this.translate.instant('alert-security-modal.automatically')}</b></p>`;

    const ref = this.modalHelper.showAlertSecurityModal(
      this.translate.instant('alert-security-modal.title'),
      this.translate.instant('alert-security-modal.stay-connected'),
      this.translate.instant('alert-security-modal.logout-now'),
      customHTML,
      true,
    );

    ref.afterClosed().subscribe(result => {
      this.modalOpen = false;
      this.clearTimers();
      if (result === 'stay') {
       this.refreshUserToken();
       
      } else {
        this.forceLogout();
      }
    });
  }

  private refreshUserToken(): void {
    this.resetIdleTimer();
    this.appState.loading = true;
    //remove the expired token immediately
    sessionStorage.removeItem('auth_token');
    //redirect the user back to shopify store
    const shopifyAddressesUrl = `https://${SHOPIFY_TOKENS.domain}/apps/sso`;
    window.location.href = shopifyAddressesUrl;
    this.start();
  }

  private forceLogout(): void {
    this.stop();
    this.modalHelper.closeAll();
    this.authService.logout().finally(() => {
      sessionStorage.removeItem('auth_token');
      localStorage.removeItem('shopifyToken');
      this.userService.clear();
      this.accountService.clearMdnsCache();
      this.router.navigate([ROUTE_URLS.LANDING]);
    });
  }
}
